import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Gift, Users, Heart, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const Home = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  return (
    <div className="max-w-6xl mx-auto">
      {/* Mobile Snap Scroll Container */}
      <div className="sm:hidden">
        <div className="snap-y snap-mandatory h-[calc(100vh-3.5rem)] overflow-y-scroll">
          {/* First Snap Point - Hero with compact icons */}
          <div className="snap-start h-[calc(100vh-3.5rem)] flex flex-col justify-center items-center px-4 py-8">
            <div className="text-center max-w-sm mx-auto">
              <div className="flex justify-center mb-4">
                <img 
                  src="/ynskje logo.svg" 
                  alt="Ynskje" 
                  className="h-20 w-20 object-contain"
                />
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-4 leading-tight">
                {t('home.title')}
              </h1>
              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                {t('home.subtitle')}
              </p>
              {user ? (
                <Link to="/dashboard" className="btn-primary text-base px-6 py-3 mb-4 w-full">
                  {t('home.goToDashboard')}
                </Link>
              ) : (
                <div className="flex flex-col gap-3 mb-4">
                  <Link to="/register" className="btn-primary text-sm px-6 py-3 w-full">
                    {t('home.getStarted')}
                  </Link>
                  <Link to="/login" className="btn-secondary text-sm px-6 py-3 w-full">
                    {t('home.signIn')}
                  </Link>
                </div>
              )}
              
              {/* Scroll indicator */}
              <div className="flex flex-col items-center text-gray-400 mt-4">
                <span className="text-xs mb-1">{t('home.scrollForMore')}</span>
                <ChevronDown className="w-5 h-5 animate-bounce" />
              </div>
            </div>
          </div>

          {/* Second Snap Point - Full features section */}
          <div className="snap-start h-[calc(100vh-3.5rem)] flex flex-col justify-center px-4 py-6">
            {/* Scroll indicator at top */}
            <div className="flex flex-col items-center text-gray-400 mb-3">
              <ChevronUp className="w-4 h-4 animate-bounce" />
              <span className="text-xs mt-1">{t('home.scrollUp')}</span>
            </div>
            
            <div className="grid grid-cols-1 gap-4 max-w-sm mx-auto">
              <div className="text-center">
                <div className="bg-primary-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <Gift className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  {t('home.createWishLists')}
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {t('home.createWishListsDesc')}
                </p>
              </div>

              <div className="text-center">
                <div className="bg-secondary-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <Users className="h-6 w-6 text-secondary-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  {t('home.shareWithFamily')}
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {t('home.shareWithFamilyDesc')}
                </p>
              </div>

              <div className="text-center">
                <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <Heart className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  {t('home.trackGifts')}
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {t('home.trackGiftsDesc')}
                </p>
              </div>
            </div>
            
            {/* Scroll indicator at bottom */}
            <div className="flex flex-col items-center text-gray-400 mt-3">
              <span className="text-xs mb-1">{t('home.scrollForMore')}</span>
              <ChevronDown className="w-4 h-4 animate-bounce" />
            </div>
          </div>
          
          {/* Third Snap Point - Footer */}
          <div className="snap-start h-[calc(100vh-3.5rem)] flex flex-col justify-center items-center px-4">
            <div className="text-center">
              <div className="text-xs text-gray-500 space-y-1">
                <div className="flex items-center justify-center gap-1">
                  <span>©</span>
                  <span>{new Date().getFullYear()}</span>
                  <span className="font-medium text-gray-600">Ynskje.no</span>
                </div>
                <div className="text-gray-400">
                  {t('home.copyright')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tablet and Desktop Version */}
      <div className="hidden sm:block">
        {/* Main Content - Fixed height, no scroll - scaling-friendly */}
        <div className="viewport-height-safe flex flex-col">
          {/* Hero Section */}
          <div className="text-center pt-8 sm:pt-10 md:pt-12 lg:pt-16 flex-shrink-0">
            <div className="flex justify-center mb-3 sm:mb-4 md:mb-5 lg:mb-6">
              <img 
                src="/ynskje logo.svg" 
                alt="Ynskje" 
                className="h-16 w-16 sm:h-20 sm:w-20 md:h-22 md:w-22 lg:h-28 lg:w-28 xl:h-32 xl:w-32 object-contain"
                style={{ height: 'clamp(3.5rem, 7vw, 7rem)', width: 'clamp(3.5rem, 7vw, 7rem)' }}
              />
            </div>
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4 px-4" style={{ fontSize: 'clamp(1.125rem, 2.75vw + 0.4rem, 2.75rem)' }}>
              {t('home.title')}
            </h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 mb-4 sm:mb-6 md:mb-8 lg:mb-10 max-w-2xl mx-auto px-4" style={{ fontSize: 'clamp(0.8125rem, 1.4vw + 0.2rem, 1.125rem)' }}>
              {t('home.subtitle')}
            </p>
            {user ? (
              <Link to="/dashboard" className="btn-primary text-sm sm:text-base md:text-lg lg:text-xl px-6 sm:px-8 md:px-10 py-2 sm:py-3">
                {t('home.goToDashboard')}
              </Link>
            ) : (
              <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-3 md:gap-4 px-4">
                <Link to="/register" className="btn-primary text-sm sm:text-base md:text-lg px-6 sm:px-8 md:px-10 py-2 sm:py-3 w-full sm:w-auto">
                  {t('home.getStarted')}
                </Link>
                <Link to="/login" className="btn-secondary text-sm sm:text-base md:text-lg px-6 sm:px-8 md:px-10 py-2 sm:py-3 w-full sm:w-auto">
                  {t('home.signIn')}
                </Link>
              </div>
            )}
          </div>

          {/* Features Section */}
          <div className="flex items-center justify-center px-4 flex-1 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 lg:gap-8 w-full max-w-4xl lg:max-w-5xl">
              <div className="text-center">
                <div className="bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3" style={{ width: 'clamp(3rem, 6vw, 4.5rem)', height: 'clamp(3rem, 6vw, 4.5rem)' }}>
                  <Gift className="text-primary-600" style={{ width: 'clamp(1.5rem, 3vw, 2rem)', height: 'clamp(1.5rem, 3vw, 2rem)' }} />
                </div>
                <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-900 mb-1 sm:mb-2" style={{ fontSize: 'clamp(0.875rem, 1.5vw + 0.25rem, 1.25rem)' }}>
                  {t('home.createWishLists')}
                </h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-600" style={{ fontSize: 'clamp(0.75rem, 1.2vw + 0.2rem, 1rem)' }}>
                  {t('home.createWishListsDesc')}
                </p>
              </div>

              <div className="text-center">
                <div className="bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3" style={{ width: 'clamp(3rem, 6vw, 4.5rem)', height: 'clamp(3rem, 6vw, 4.5rem)' }}>
                  <Users className="text-secondary-600" style={{ width: 'clamp(1.5rem, 3vw, 2rem)', height: 'clamp(1.5rem, 3vw, 2rem)' }} />
                </div>
                <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-900 mb-1 sm:mb-2" style={{ fontSize: 'clamp(0.875rem, 1.5vw + 0.25rem, 1.25rem)' }}>
                  {t('home.shareWithFamily')}
                </h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-600" style={{ fontSize: 'clamp(0.75rem, 1.2vw + 0.2rem, 1rem)' }}>
                  {t('home.shareWithFamilyDesc')}
                </p>
              </div>

              <div className="text-center">
                <div className="bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3" style={{ width: 'clamp(3rem, 6vw, 4.5rem)', height: 'clamp(3rem, 6vw, 4.5rem)' }}>
                  <Heart className="text-green-600" style={{ width: 'clamp(1.5rem, 3vw, 2rem)', height: 'clamp(1.5rem, 3vw, 2rem)' }} />
                </div>
                <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-900 mb-1 sm:mb-2" style={{ fontSize: 'clamp(0.875rem, 1.5vw + 0.25rem, 1.25rem)' }}>
                  {t('home.trackGifts')}
                </h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-600" style={{ fontSize: 'clamp(0.75rem, 1.2vw + 0.2rem, 1rem)' }}>
                  {t('home.trackGiftsDesc')}
                </p>
              </div>
            </div>
          </div>

          {/* Copyright Footer - Desktop only */}
          <div className="text-center py-2 sm:py-3 border-t border-gray-200 flex-shrink-0">
            <div className="text-xs sm:text-sm text-gray-500 space-y-1">
              <div className="flex items-center justify-center gap-1">
                <span>©</span>
                <span>{new Date().getFullYear()}</span>
                <span className="font-medium text-gray-600">Ynskje.no</span>
              </div>
              <div className="text-gray-400">
                {t('home.copyright')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
